// src/utils/validationHelpers.ts
// Main validation utility functions used by forms

import { VALIDATION_RULES, CONDITIONAL_RULES } from './validationRules';
import type { RefObject } from 'react';
import { FIELD_MESSAGES, VALIDATION_MESSAGES } from './validationMessages';
import { API_ERROR_MESSAGES } from './validationMessages';

/**
 * Generic form errors type
 */
export type FormErrors<T> = {
  [K in keyof T]?: string;
};

/**
 * Validation result type
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Core validation functions that return error messages
 */
export const validators = {
  /**
   * Validate required field
   * @param value - The field value
   * @param fieldName - The name of the field for error message
   * @returns Error message or undefined if valid
   */
  validateRequired: (
    value: string | undefined | null,
    fieldName: string
  ): string | undefined => {
    return VALIDATION_RULES.isRequired(value)
      ? undefined
      : VALIDATION_MESSAGES.required(fieldName);
  },

  /**
   * Validate email field (required + format)
   * @param email - The email value
   * @returns Error message or undefined if valid
   */
  validateEmail: (email: string | undefined | null): string | undefined => {
    if (!VALIDATION_RULES.isRequired(email)) {
      return FIELD_MESSAGES.email;
    }
    if (!VALIDATION_RULES.isValidEmail(email!)) {
      return VALIDATION_MESSAGES.emailInvalid;
    }
    return undefined;
  },

  /**
   * Validate phone field (required + format)
   * @param phone - The phone value
   * @returns Error message or undefined if valid
   */
  validatePhone: (phone: string | undefined | null): string | undefined => {
    if (!VALIDATION_RULES.isRequired(phone)) {
      return FIELD_MESSAGES.phone;
    }
    // Note: Phone format validation is lenient in current app
    // Can add format validation here if needed:
    // if (!VALIDATION_RULES.isValidPhone(phone!)) {
    //   return VALIDATION_MESSAGES.phoneInvalid;
    // }
    return undefined;
  },

  /**
   * Validate select/dropdown field
   * @param value - The selected value
   * @param fieldName - The name of the field
   * @returns Error message or undefined if valid
   */
  validateSelect: (
    value: string | undefined | null,
    fieldName: string
  ): string | undefined => {
    return VALIDATION_RULES.isSelected(value)
      ? undefined
      : VALIDATION_MESSAGES.select(fieldName);
  },

  /**
   * Validate required date field
   * @param date - The date value
   * @param fieldName - The name of the field
   * @returns Error message or undefined if valid
   */
  validateRequiredDate: (
    date: string | undefined | null,
    fieldName: string
  ): string | undefined => {
    return VALIDATION_RULES.isRequired(date)
      ? undefined
      : VALIDATION_MESSAGES.date(fieldName);
  },

  /**
   * Validate positive number field
   * @param value - The numeric value
   * @param fieldName - The name of the field
   * @returns Error message or undefined if valid
   */
  validatePositiveNumber: (
    value: string | number | undefined | null,
    fieldName: string
  ): string | undefined => {
    if (!VALIDATION_RULES.isRequired(String(value))) {
      return VALIDATION_MESSAGES.required(fieldName);
    }
    if (!VALIDATION_RULES.isPositiveNumber(value!)) {
      return VALIDATION_MESSAGES.positiveNumber(fieldName);
    }
    return undefined;
  },

  /**
   * Validate conditional required field
   * @param value - The field value
   * @param condition - Whether the field is required
   * @param fieldName - The name of the field
   * @param conditionDescription - Description of the condition
   * @returns Error message or undefined if valid
   */
  validateConditionalRequired: (
    value: string | undefined | null,
    condition: boolean,
    fieldName: string,
    conditionDescription: string
  ): string | undefined => {
    const isValid = CONDITIONAL_RULES.requiredIf(value, condition);
    return isValid
      ? undefined
      : VALIDATION_MESSAGES.conditionalRequired(
          fieldName,
          conditionDescription
        );
  },

  /**
   * Validate date range (end date must be after start date)
   * @param startDate - The start date
   * @param endDate - The end date
   * @param startFieldName - Name of start field
   * @param endFieldName - Name of end field
   * @returns Object with errors for both fields or undefined if valid
   */
  validateDateRange: (
    startDate: string | undefined | null,
    endDate: string | undefined | null,
    startFieldName: string,
    endFieldName: string
  ): { startError?: string; endError?: string } => {
    const errors: { startError?: string; endError?: string } = {};

    // Check if dates are required
    if (!VALIDATION_RULES.isRequired(startDate)) {
      errors.startError = VALIDATION_MESSAGES.date(startFieldName);
    }
    if (!VALIDATION_RULES.isRequired(endDate)) {
      errors.endError = VALIDATION_MESSAGES.date(endFieldName);
    }

    // If both dates exist, check if end is after start
    if (
      VALIDATION_RULES.isRequired(startDate) &&
      VALIDATION_RULES.isRequired(endDate) &&
      !VALIDATION_RULES.isDateAfter(startDate!, endDate!)
    ) {
      errors.endError = VALIDATION_MESSAGES.dateRange(
        startFieldName,
        endFieldName
      );
    }

    return errors;
  },
};

/**
 * Form-specific validation helpers
 * uses FIELD_MESSAGES for specific, customizable messages
 */
export const formValidators = {
  /**
   * Validate Client Form
   */
  client: {
    firstName: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isRequired(value) ? undefined : FIELD_MESSAGES.firstName,

    lastName: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isRequired(value) ? undefined : FIELD_MESSAGES.lastName,

    email: (value: string | undefined): string | undefined =>
      validators.validateEmail(value),

    phone: (value: string | undefined): string | undefined =>
      validators.validatePhone(value),

    vetOther: (
      value: string | undefined,
      hasVetOther: boolean
    ): string | undefined =>
      CONDITIONAL_RULES.requiredIf(value, hasVetOther)
        ? undefined
        : FIELD_MESSAGES.vetOther,

    notes: (
      value: string | undefined,
      showWarning: boolean
    ): string | undefined =>
      CONDITIONAL_RULES.requiredIf(value, showWarning)
        ? undefined
        : FIELD_MESSAGES.notes,
  },

  /**
   * Validate Pet Form
   */
  pet: {
    name: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isRequired(value) ? undefined : FIELD_MESSAGES.petName,

    petType: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isSelected(value) ? undefined : FIELD_MESSAGES.petType,

    petSize: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isSelected(value) ? undefined : FIELD_MESSAGES.petSize,

    petBreed: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isSelected(value) ? undefined : FIELD_MESSAGES.petBreed,

    gender: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isSelected(value) ? undefined : FIELD_MESSAGES.gender,

    notes: (
      value: string | undefined,
      showWarning: boolean
    ): string | undefined =>
      CONDITIONAL_RULES.requiredIf(value, showWarning)
        ? undefined
        : FIELD_MESSAGES.petNotes,
  },

  /**
   * Validate Login Form
   * Uses FIELD_MESSAGES for centralized messages
   */
  login: {
    username: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isRequired(value) ? undefined : FIELD_MESSAGES.username,

    password: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isRequired(value) ? undefined : FIELD_MESSAGES.password,
  },

  /**
   * Validate Booking Form
   */
  booking: {
    petId: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isSelected(value)
        ? undefined
        : FIELD_MESSAGES.petSelection,

    checkInDate: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isRequired(value)
        ? undefined
        : FIELD_MESSAGES.checkInDate,

    checkOutDate: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isRequired(value)
        ? undefined
        : FIELD_MESSAGES.checkOutDate,

    dateRange: (
      checkInDate: string | undefined,
      checkOutDate: string | undefined
    ) =>
      validators.validateDateRange(
        checkInDate,
        checkOutDate,
        'Check-in date',
        'Check-out date'
      ),

    dailyRate: (value: string | number | undefined): string | undefined => {
      if (!VALIDATION_RULES.isRequired(String(value))) {
        return FIELD_MESSAGES.dailyRate;
      }
      if (!VALIDATION_RULES.isPositiveNumber(value!)) {
        return FIELD_MESSAGES.validDailyRate;
      }
      return undefined;
    },
  },

  /**
   * Validate Surcharge Form
   */
  surcharge: {
    reason: (value: string | undefined): string | undefined =>
      VALIDATION_RULES.isRequired(value)
        ? undefined
        : FIELD_MESSAGES.surchargeReason,

    amount: (value: string | number | undefined): string | undefined => {
      if (!VALIDATION_RULES.isRequired(String(value))) {
        return FIELD_MESSAGES.surchargeAmount;
      }
      if (!VALIDATION_RULES.isPositiveNumber(value!)) {
        return FIELD_MESSAGES.surchargeAmountInvalid;
      }
      return undefined;
    },
  },
};

/**
 * Utility function to check if a form has any errors
 * @param errors - The errors object
 * @returns True if there are no errors
 */
export const isFormValid = <T extends object>(
  errors: FormErrors<T>
): boolean => {
  return Object.values(errors).every((error) => !error);
};

/**
 * Utility function to clear a specific field error
 * @param errors - Current errors object
 * @param field - Field to clear
 * @returns New errors object with field cleared
 */
export const clearFieldError = <T extends object>(
  errors: FormErrors<T>,
  field: keyof T
): FormErrors<T> => {
  const newErrors = { ...errors };
  delete newErrors[field];
  return newErrors;
};

/**
 * Utility function to set a field error
 * @param errors - Current errors object
 * @param field - Field to set error on
 * @param message - Error message
 * @returns New errors object with error set
 */
export const setFieldError = <T extends object>(
  errors: FormErrors<T>,
  field: keyof T,
  message: string
): FormErrors<T> => {
  return {
    ...errors,
    [field]: message,
  };
};

/**
 * Utility to validate multiple fields at once
 * @param validations - Array of validation functions that return error messages
 * @returns Array of error messages (undefined for valid fields)
 */
export const validateFields = (
  validations: Array<() => string | undefined>
): Array<string | undefined> => {
  return validations.map((validate) => validate());
};

/**
 * Utility to create an errors object from field validators
 * @param fieldValidations - Object mapping field names to validation functions
 * @returns Errors object
 */
export const createErrorsObject = <T extends object>(fieldValidations: {
  [K in keyof T]?: () => string | undefined;
}): FormErrors<T> => {
  const errors: FormErrors<T> = {};

  Object.keys(fieldValidations).forEach((key) => {
    const field = key as keyof T;
    const validate = fieldValidations[field];
    if (validate) {
      const error = validate();
      if (error) {
        errors[field] = error;
      }
    }
  });

  return errors;
};

/**
 * Scroll to the first field with an error
 * @param errors - The errors object
 * @param fieldRefs - Map of field names to their refs
 * @param offset - Optional offset from top (useful for sticky headers)
 * @returns The field key that was scrolled to, or undefined if none
 */
export const scrollToFirstError = <T extends object>(
  errors: FormErrors<T>,
  fieldRefs: Map<keyof T, RefObject<HTMLElement>>,
  offset: number = 100
): keyof T | undefined => {
  // Get all fields with errors
  const errorFields = Object.keys(errors).filter(
    (key) => errors[key as keyof T]
  ) as Array<keyof T>;

  if (errorFields.length === 0) {
    return undefined;
  }

  // Find the first field that has a ref
  for (const fieldKey of errorFields) {
    const ref = fieldRefs.get(fieldKey);
    if (ref?.current) {
      // Calculate scroll position with offset
      const element = ref.current;
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      // Smooth scroll to the field
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      // Optional: Focus the field after a short delay (for text inputs)
      setTimeout(() => {
        if (
          element instanceof HTMLInputElement ||
          element instanceof HTMLTextAreaElement
        ) {
          element.focus();
        }
      }, 300);

      return fieldKey;
    }
  }

  return undefined;
};

/**
 * Transform API errors into user-friendly form errors
 * Useful for handling validation errors from the backend
 */
export const transformApiErrors = (
  apiError: any
): Record<string, string> | null => {
  // Handle backend validation errors (422 status with errors object)
  if (apiError?.response?.data?.errors) {
    const errors = apiError.response.data.errors;
    const formErrors: Record<string, string> = {};

    // Transform backend field errors to form field format
    Object.keys(errors).forEach((key) => {
      const fieldErrors = errors[key];
      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        // Convert PascalCase/camelCase to match form field names
        const fieldName = key.charAt(0).toLowerCase() + key.slice(1);
        formErrors[fieldName] = fieldErrors[0]; // Take first error message
      }
    });

    return Object.keys(formErrors).length > 0 ? formErrors : null;
  }

  return null;
};

/**
 * Get user-friendly error message from API error
 */
export const getApiErrorMessage = (
  error: any,
  operation: keyof typeof API_ERROR_MESSAGES
): string => {
  // Try to get message from error response
  const responseMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.userMessage;

  // Return specific message or fallback to operation message
  return responseMessage || API_ERROR_MESSAGES[operation];
};
